import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Rating,
  Pagination,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Tabs,
  Tab,
  Badge,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import TvIcon from '@mui/icons-material/Tv';
import TabletIcon from '@mui/icons-material/Tablet';
import MemoryIcon from '@mui/icons-material/Memory';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#ffffff',
  padding: theme.spacing(8, 0),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'linear-gradient(60deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
    zIndex: 1,
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
}));

const SearchBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  backgroundColor: '#f8f8f8',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

const FilterBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: '#f8f8f8',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
  },
}));

const ProductImage = styled(CardMedia)(({ theme }) => ({
  height: 180,
  backgroundSize: 'contain',
  backgroundColor: '#f5f5f5',
}));

const PriceTag = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: '#FFE302',
  color: '#000000',
  fontWeight: 'bold',
}));

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#FFE302',
  },
});

// Mock data for parts
const mockParts = [
  {
    id: 1,
    name: 'iPhone 13 Pro OLED Display',
    image: 'https://via.placeholder.com/300x200?text=iPhone+Display',
    price: 129.99,
    rating: 4.5,
    reviews: 128,
    category: 'Phone Parts',
    compatibility: ['iPhone 13 Pro', 'iPhone 13 Pro Max'],
    inStock: true,
    discount: 10,
    featured: true,
  },
  {
    id: 2,
    name: 'Samsung Galaxy S21 Battery',
    image: 'https://via.placeholder.com/300x200?text=Samsung+Battery',
    price: 49.99,
    rating: 4.2,
    reviews: 95,
    category: 'Phone Parts',
    compatibility: ['Samsung Galaxy S21', 'Samsung Galaxy S21+'],
    inStock: true,
    discount: 0,
    featured: false,
  },
  {
    id: 3,
    name: 'MacBook Pro 2021 Keyboard',
    image: 'https://via.placeholder.com/300x200?text=MacBook+Keyboard',
    price: 149.99,
    rating: 4.7,
    reviews: 56,
    category: 'Laptop Parts',
    compatibility: ['MacBook Pro 14" 2021', 'MacBook Pro 16" 2021'],
    inStock: true,
    discount: 15,
    featured: true,
  },
  {
    id: 4,
    name: 'Dell XPS 15 Cooling Fan',
    image: 'https://via.placeholder.com/300x200?text=Dell+Fan',
    price: 39.99,
    rating: 4.0,
    reviews: 42,
    category: 'Laptop Parts',
    compatibility: ['Dell XPS 15 9500', 'Dell XPS 15 9510'],
    inStock: false,
    discount: 0,
    featured: false,
  },
  {
    id: 5,
    name: 'iPad Pro 12.9" Screen Protector',
    image: 'https://via.placeholder.com/300x200?text=iPad+Protector',
    price: 29.99,
    rating: 4.3,
    reviews: 87,
    category: 'Tablet Parts',
    compatibility: ['iPad Pro 12.9" (5th Gen)', 'iPad Pro 12.9" (4th Gen)'],
    inStock: true,
    discount: 5,
    featured: false,
  },
  {
    id: 6,
    name: 'NVIDIA RTX 3080 GPU',
    image: 'https://via.placeholder.com/300x200?text=NVIDIA+GPU',
    price: 699.99,
    rating: 4.9,
    reviews: 203,
    category: 'Computer Parts',
    compatibility: ['Custom PC Builds', 'Gaming Desktops'],
    inStock: true,
    discount: 0,
    featured: true,
  },
  {
    id: 7,
    name: 'Samsung 4K TV Main Board',
    image: 'https://via.placeholder.com/300x200?text=TV+Board',
    price: 189.99,
    rating: 4.1,
    reviews: 32,
    category: 'TV Parts',
    compatibility: ['Samsung Q70 Series', 'Samsung Q80 Series'],
    inStock: true,
    discount: 0,
    featured: false,
  },
  {
    id: 8,
    name: 'Sony PlayStation 5 Power Supply',
    image: 'https://via.placeholder.com/300x200?text=PS5+Power',
    price: 89.99,
    rating: 4.6,
    reviews: 64,
    category: 'Console Parts',
    compatibility: ['PlayStation 5 Digital Edition', 'PlayStation 5 Disc Edition'],
    inStock: true,
    discount: 0,
    featured: false,
  },
];

// Categories with icons
const categories = [
  { name: 'Phone Parts', icon: <PhoneIphoneIcon /> },
  { name: 'Laptop Parts', icon: <LaptopIcon /> },
  { name: 'TV Parts', icon: <TvIcon /> },
  { name: 'Tablet Parts', icon: <TabletIcon /> },
  { name: 'Computer Parts', icon: <MemoryIcon /> },
  { name: 'Batteries', icon: <BatteryChargingFullIcon /> },
  { name: 'Displays', icon: <ScreenshotMonitorIcon /> },
  { name: 'Console Parts', icon: <SortIcon /> },
];

const PartsMarketplace = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // State variables
  const [parts, setParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortOption, setSortOption] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [favorites, setFavorites] = useState([]);

  const itemsPerPage = 8;

  // Load parts data
  useEffect(() => {
    // Simulate API call with timeout
    const timer = setTimeout(() => {
      setParts(mockParts);
      setFilteredParts(mockParts);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Filter and sort parts
  useEffect(() => {
    let result = [...parts];

    // Filter by search query
    if (searchQuery) {
      result = result.filter(part => 
        part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        part.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      result = result.filter(part => part.category === selectedCategory);
    }

    // Filter by price range
    result = result.filter(part => 
      part.price >= priceRange[0] && part.price <= priceRange[1]
    );

    // Filter by stock
    if (inStockOnly) {
      result = result.filter(part => part.inStock);
    }

    // Sort results
    switch (sortOption) {
      case 'priceLow':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'priceHigh':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        // In a real app, you'd sort by date
        result.sort((a, b) => b.id - a.id);
        break;
      case 'featured':
      default:
        result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
    }

    setFilteredParts(result);
    setPage(1); // Reset to first page when filters change
  }, [parts, searchQuery, selectedCategory, sortOption, priceRange, inStockOnly]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setSelectedCategory('All');
    } else {
      setSelectedCategory(categories[newValue - 1].name);
    }
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Add to cart
  const handleAddToCart = (part) => {
    const existingItem = cartItems.find(item => item.id === part.id);
    
    if (existingItem) {
      setCartItems(cartItems.map(item => 
        item.id === part.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCartItems([...cartItems, { ...part, quantity: 1 }]);
    }
  };

  // Toggle favorite
  const handleToggleFavorite = (partId) => {
    if (favorites.includes(partId)) {
      setFavorites(favorites.filter(id => id !== partId));
    } else {
      setFavorites([...favorites, partId]);
    }
  };

  // View part details
  const handleViewPart = (partId) => {
    navigate(`/part/${partId}`);
  };

  // Calculate pagination
  const indexOfLastPart = page * itemsPerPage;
  const indexOfFirstPart = indexOfLastPart - itemsPerPage;
  const currentParts = filteredParts.slice(indexOfFirstPart, indexOfLastPart);
  const pageCount = Math.ceil(filteredParts.length / itemsPerPage);

  return (
    <>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <Container maxWidth="lg">
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Technical Parts Marketplace
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
              Find genuine replacement parts for your devices with fast shipping and expert support
            </Typography>
            
            {/* Search Bar */}
            <Box sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search for parts by name, model, or category..."
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'white',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent',
                    },
                  }
                }}
              />
            </Box>
            
            {/* Featured Categories */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 4 }}>
              {categories.slice(0, 4).map((category) => (
                <Button
                  key={category.name}
                  variant="contained"
                  startIcon={category.icon}
                  onClick={() => setSelectedCategory(category.name)}
                  sx={{
                    backgroundColor: '#FFE302',
                    color: '#000000',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#FFD700',
                    },
                    px: 2,
                    py: 1,
                  }}
                >
                  {category.name}
                </Button>
              ))}
            </Box>
          </Container>
        </HeroContent>
      </HeroSection>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Category Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#FFE302',
              },
              '& .Mui-selected': {
                color: '#000000 !important',
                fontWeight: 'bold',
              },
            }}
          >
            <Tab label="All Parts" />
            {categories.map((category) => (
              <Tab key={category.name} icon={category.icon} label={category.name} />
            ))}
          </Tabs>
        </Paper>
        
        {/* Filters and Sorting */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <FilterBox>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Filters
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              {/* Category Filter */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="All">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.name} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Sort Options */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="featured">Featured</MenuItem>
                  <MenuItem value="priceLow">Price: Low to High</MenuItem>
                  <MenuItem value="priceHigh">Price: High to Low</MenuItem>
                  <MenuItem value="rating">Highest Rated</MenuItem>
                  <MenuItem value="newest">Newest Arrivals</MenuItem>
                </Select>
              </FormControl>
              
              {/* In Stock Only */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button
                  variant={inStockOnly ? "contained" : "outlined"}
                  onClick={() => setInStockOnly(!inStockOnly)}
                  startIcon={<VerifiedIcon />}
                  sx={{
                    backgroundColor: inStockOnly ? '#FFE302' : 'transparent',
                    color: inStockOnly ? '#000000' : 'inherit',
                    borderColor: '#FFE302',
                    '&:hover': {
                      backgroundColor: inStockOnly ? '#FFD700' : 'rgba(255, 227, 2, 0.1)',
                      borderColor: '#FFE302',
                    },
                    width: '100%',
                  }}
                >
                  In Stock Only
                </Button>
              </Box>
              
              {/* Quick Links */}
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                Quick Links
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  startIcon={<StarIcon />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Featured Deals
                </Button>
                <Button 
                  startIcon={<LocalShippingIcon />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Free Shipping Items
                </Button>
                <Button 
                  startIcon={<FavoriteIcon />}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  My Wishlist
                </Button>
              </Box>
            </FilterBox>
          </Grid>
          
          <Grid item xs={12} md={9}>
            {/* Results Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {loading ? 'Loading parts...' : `${filteredParts.length} parts found`}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge badgeContent={cartItems.length} color="primary" sx={{ mr: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => navigate('/cart')}
                    sx={{
                      backgroundColor: '#000000',
                      color: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#333333',
                      },
                    }}
                  >
                    Cart
                  </Button>
                </Badge>
              </Box>
            </Box>
            
            {/* Parts Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#FFE302' }} />
              </Box>
            ) : filteredParts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6">No parts found matching your criteria</Typography>
                <Button 
                  variant="contained"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All');
                    setSortOption('featured');
                    setInStockOnly(false);
                  }}
                  sx={{ 
                    mt: 2,
                    backgroundColor: '#FFE302',
                    color: '#000000',
                    '&:hover': {
                      backgroundColor: '#FFD700',
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {currentParts.map((part) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={part.id}>
                      <ProductCard>
                        {part.discount > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              backgroundColor: '#FFE302',
                              color: '#000000',
                              fontWeight: 'bold',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              zIndex: 1,
                            }}
                          >
                            {part.discount}% OFF
                          </Box>
                        )}
                        
                        <ProductImage
                          image={part.image}
                          title={part.name}
                        />
                        
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {part.name}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <StyledRating
                              value={part.rating}
                              precision={0.5}
                              size="small"
                              readOnly
                            />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              ({part.reviews})
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 1 }}>
                            <CategoryChip
                              label={part.category}
                              size="small"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {part.inStock ? (
                              <Typography variant="body2" sx={{ color: 'success.main' }}>
                                In Stock
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'error.main' }}>
                                Out of Stock
                              </Typography>
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {part.discount > 0 ? (
                              <>
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}>
                                  ${part.price.toFixed(2)}
                                </Typography>
                                <PriceTag variant="h6">
                                  ${(part.price * (1 - part.discount / 100)).toFixed(2)}
                                </PriceTag>
                              </>
                            ) : (
                              <PriceTag variant="h6">
                                ${part.price.toFixed(2)}
                              </PriceTag>
                            )}
                          </Box>
                        </CardContent>
                        
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddShoppingCartIcon />}
                            onClick={() => handleAddToCart(part)}
                            disabled={!part.inStock}
                            sx={{
                              backgroundColor: '#000000',
                              color: '#ffffff',
                              '&:hover': {
                                backgroundColor: '#333333',
                              },
                              flexGrow: 1,
                            }}
                          >
                            Add to Cart
                          </Button>
                          
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFavorite(part.id)}
                            sx={{ ml: 1 }}
                          >
                            {favorites.includes(part.id) ? (
                              <FavoriteIcon sx={{ color: '#ff6b6b' }} />
                            ) : (
                              <FavoriteBorderIcon />
                            )}
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            onClick={() => handleViewPart(part.id)}
                            sx={{ ml: 1 }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </CardActions>
                      </ProductCard>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Pagination */}
                {pageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: '#000000',
                        },
                        '& .Mui-selected': {
                          backgroundColor: '#FFE302 !important',
                          color: '#000000',
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
      
      {/* Features Section */}
      <Box sx={{ backgroundColor: '#f8f8f8', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <VerifiedIcon sx={{ fontSize: 48, color: '#FFE302', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Genuine Parts
                </Typography>
                <Typography variant="body2">
                  All parts are genuine or high-quality compatible replacements, tested to meet or exceed OEM specifications.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <LocalShippingIcon sx={{ fontSize: 48, color: '#FFE302', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Fast Shipping
                </Typography>
                <Typography variant="body2">
                  Most orders ship within 24 hours. Free shipping on orders over $50 and expedited options available.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <BuildIcon sx={{ fontSize: 48, color: '#FFE302', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Expert Support
                </Typography>
                <Typography variant="body2">
                  Our technical team is available to help with part selection, installation advice, and troubleshooting.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Footer />
    </>
  );
};

export default PartsMarketplace;